import React, { useState, useRef, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { ConversationMedia } from '../types/media.types';
import { mediaService } from '../services/mediaService';
import { logger } from '../utils/logger';
import {
  getDocumentDisplayCaption,
  getDocumentDownloadName,
} from '../utils/mediaMessageDisplay';

interface MediaMessageProps {
  media: ConversationMedia;
  isFromClient?: boolean;
  messageText?: string;
  originalFileName?: string;
}

const MediaMessage: React.FC<MediaMessageProps> = ({
  media,
  isFromClient = false,
  messageText,
  originalFileName,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Iniciar true para exibir imagem/áudio imediatamente (evita ficar em "Carregando..." se o observer atrasar)
  const [isInView, setIsInView] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);

  // Debug log removido para limpeza

  const handlePlayPause = () => {
    if (media.type === 'audio') {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    }
  };

  const handleImageClick = () => {
    if (media.type === 'image') {
      // Imagens: abrir em nova aba usando rota pública /media/:mediaId
      window.open(mediaService.getMediaUrl(media.id), '_blank');
    }
  };

  const handleDocumentClick = async () => {
    if (media.type !== 'document') return;

    try {
      const originalName = getDocumentDownloadName(
        messageText,
        media.file_url,
        originalFileName
      );
      const url = mediaService.getMediaUrl(media.id, originalName);
      const response = await fetch(url);
      if (!response.ok) {
        logger.error('Erro ao baixar documento:', response.status, response.statusText);
        return;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar URL de blob
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      logger.error('Erro inesperado ao baixar documento:', error);
    }
  };

  const handleVideoClick = () => {
    if (media.type === 'video') {
      // Abrir vídeo em nova aba para visualização ampliada
      window.open(mediaService.getMediaUrl(media.id), '_blank');
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Intersection Observer opcional para lazy loading (isInView já inicia true para exibir logo)
  useEffect(() => {
    if (!mediaRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(mediaRef.current);
    return () => observer.disconnect();
  }, []);

  // Preload de áudio quando estiver em view
  useEffect(() => {
    if (isInView && media.type === 'audio' && audioRef.current) {
      audioRef.current.preload = 'metadata';
    }
  }, [isInView, media.type]);

  return (
    <div ref={mediaRef} className={`flex ${isFromClient ? 'justify-start' : 'justify-end'} mb-2`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
        isFromClient 
          ? 'bg-gray-200 text-gray-800' 
          : 'bg-blue-500 text-white'
      }`}>
        {media.type === 'audio' && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handlePlayPause}
              disabled={isLoading}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isFromClient 
                  ? 'bg-gray-300 hover:bg-gray-400' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } transition-colors`}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              )}
            </button>
            <div className="flex-1">
              <div className="text-xs opacity-75">
                {formatTimestamp(media.timestamp)}
              </div>
            </div>
            <audio
              ref={audioRef}
              src={mediaService.getMediaUrl(media.id)}
              onEnded={handleAudioEnded}
              onLoadStart={() => setIsLoading(true)}
              onCanPlay={() => setIsLoading(false)}
              preload="metadata"
            />
          </div>
        )}

        {media.type === 'image' && (
          <div className="cursor-pointer" onClick={handleImageClick}>
            <div className="relative">
              {isInView ? (
                <img
                  src={mediaService.getMediaUrl(media.id)}
                  alt="Imagem"
                  className="max-w-full h-auto rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-gray-500 text-sm">Carregando...</div>
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="mt-2 text-sm">
              {messageText && messageText.trim() && (
                <div className="mt-2 p-2 bg-black bg-opacity-10 rounded text-sm whitespace-pre-wrap break-words">
                  {messageText}
                </div>
              )}
              <div className="text-xs opacity-75 mt-2">
                {formatTimestamp(media.timestamp)}
              </div>
            </div>
          </div>
        )}

        {media.type === 'document' && (() => {
          const documentCaption = getDocumentDisplayCaption(messageText, media.file_url);
          return (
            <div>
              <button
                type="button"
                onClick={handleDocumentClick}
                className={`flex items-center gap-2 w-full text-left rounded-md transition-colors ${
                  isFromClient ? 'hover:bg-gray-300/60' : 'hover:bg-blue-600/80'
                }`}
                title="Baixar documento"
              >
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded flex items-center justify-center ${
                    isFromClient ? 'bg-gray-300 text-gray-700' : 'bg-blue-600 text-white'
                  }`}
                >
                  <FileText className="w-5 h-5" aria-hidden />
                </div>
                <span className="text-sm font-medium">Documento</span>
              </button>
              {documentCaption && (
                <p className="text-sm mt-2 whitespace-pre-wrap break-words">{documentCaption}</p>
              )}
              <div className="text-xs opacity-75 mt-1">
                {formatTimestamp(media.timestamp)}
              </div>
            </div>
          );
        })()}

        {media.type === 'video' && (
          <div className="cursor-pointer" onClick={handleVideoClick}>
            <div className="relative">
              {isInView ? (
                <video
                  src={mediaService.getMediaUrl(media.id)}
                  controls
                  className="max-w-full h-auto rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  preload="metadata"
                  onError={(e) => {
                    logger.error('Erro ao carregar vídeo:', e);
                  }}
                >
                  Seu navegador não suporta a tag de vídeo.
                </video>
              ) : (
                <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-gray-500 text-sm">Carregando vídeo...</div>
                </div>
              )}
            </div>
            <div className="mt-2 text-sm">
              {messageText && messageText.trim() && (
                <div className="mt-2 p-2 bg-black bg-opacity-10 rounded text-sm whitespace-pre-wrap break-words">
                  {messageText}
                </div>
              )}
              <div className="text-xs opacity-75 mt-2">
                {formatTimestamp(media.timestamp)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaMessage;
